// src/pages/IntentSelectorPage.jsx
//
// AI PRIME Command Center — the post-login launcher. ONE scope-adaptive
// component: it composes entirely from GET /api/v1/command-center, which
// returns only the sections for products the account is entitled to. DH (all
// products) gets the full view; a PadRekko-only customer (UL) gets the lean
// view from this same code path. Every number is real (server-side, demo-safe)
// — there are no mock constants here. See docs/AIPRIME_COMMAND_CENTER_RECON.md.

import React, { useEffect, useMemo, useState } from "react";
import {
  Clock, Crosshair, Layers, ArrowRight, ChevronRight, Plus, Loader2, Activity, FolderOpen, History,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { useAppNav } from "../hooks/useAppNav";
import { useProjectStore } from "../stores/useProjectStore";
import { useSiteStore } from "../stores/useSiteStore";
import { useOrgStore } from "../stores/useOrgStore";
import { useLastSessionStore } from "../stores/useLastSessionStore";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { PROJECT_TYPES, projectTypeLabel, projectTypeTooltip, UNSPECIFIED_LABEL } from "../constants/projectTypes";

// Resume-banner product labels (last-session slug → display name).
const RESUME_LABEL = {
  padrekko: "PadRekko", siteid: "AssetID", well_finder: "WellFinder",
  map: "Explore", explore: "Explore", purchase: "Purchase Imagery", helios: "AIAssist",
};

/* ── product display metadata (view-only; composition is data-driven) ── */
const PRODUCT_META = {
  PADREKKO: {
    slug: "padrekko", label: "PadRekko", Icon: Clock,
    accent: "#fbbf24", chip: "bg-amber-500/15 text-amber-300",
    desc: "Pad reclamation QC — lifecycle tracking from planning to reclamation.",
    stats: [["tracked", "Tracked"], ["overdue", "Overdue", "warn"], ["closed", "Closed", "good"]],
  },
  WELLFINDER: {
    slug: "well_finder", label: "WellFinder", Icon: Crosshair,
    accent: "#16f2d4", chip: "bg-teal-500/15 text-teal-200",
    desc: "Reconcile RRC records against AI detections — surface matches and gaps.",
    stats: [["matched", "Matched", "good"], ["discrepancies", "Discrep.", "warn"], ["unknown", "Unknown"]],
  },
  ASSETID: {
    slug: "siteid", label: "AssetID", Icon: Layers,
    accent: "#1f9dff", chip: "bg-sky-500/15 text-sky-200",
    desc: "Site asset intelligence — classify physical features from high-res imagery.",
    stats: [["classified", "Classified", "good"]],
  },
};
const PRODUCT_ORDER = ["PADREKKO", "WELLFINDER", "ASSETID"];

const KPI_META = [
  ["active_projects", "Active projects"],
  ["sites_indexed", "Sites indexed"],
  ["open_obligations", "Open obligations", true],
  ["pending_reviews", "Pending reviews", true],
  ["latest_imagery", "Latest imagery"],
];

function fmtEpoch(v) {
  if (!v) return "—";
  // Day optional and ignored - this renders via toLocaleDateString, which is
  // deliberately NOT the canonical formatter's fixed "Jun 2025" shape, so it
  // is widened rather than delegated.
  const m = /^(\d{4})_(\d{2})(?:_(\d{2}))?$/.exec(String(v));
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 172800) return "yest";
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtTitleCase(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function IntentSelectorPage() {
  const nav = useAppNav();
  const navigate = useNavigate();
  const setActiveProject = useSiteStore((s) => s.setActiveProject);
  const siteIdEnabled = useSiteStore((s) => s.siteIdEnabled);
  const toggleSiteId = useSiteStore((s) => s.toggleSiteId);
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveProjectId = useProjectStore((s) => s.setActiveProjectId);
  const projects = useProjectStore((s) => s.projects);
  const orgRole = useOrgStore((s) => s.role);
  const canCreateProject = orgRole === "org_admin" || orgRole === "org_analyst";

  // Resume last session — restore the snapshotted project + product tab.
  const lastProjectId = useLastSessionStore((s) => s.projectId);
  const lastProduct = useLastSessionStore((s) => s.product);
  const resumeProjectName = useMemo(
    () => (lastProjectId ? projects.find((p) => p.id === lastProjectId)?.name ?? null : null),
    [lastProjectId, projects]
  );

  const [cc, setCc] = useState(null);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // portfolio type filter (client-side)

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/v1/command-center");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (live) setCc(data);
      } catch (err) {
        if (live) setError(err?.message ?? String(err));
      }
    })();
    return () => { live = false; };
  }, []);

  // Launch a product tab (AssetID/siteid needs its store toggle).
  function launch(slug) {
    if (slug === "siteid" && !siteIdEnabled) {
      setActiveProject(activeProjectId);
      toggleSiteId();
    }
    nav.goMapTab(slug);
  }
  function openProject(id) {
    setActiveProjectId(id);
    navigate({ to: "/projects/$projectId", params: { projectId: id } });
  }
  function handleResume() {
    if (!lastProduct) return;
    if (lastProjectId) setActiveProjectId(lastProjectId);
    if (lastProduct === "siteid" && !siteIdEnabled) {
      setActiveProject(lastProjectId ?? activeProjectId);
      toggleSiteId();
    }
    launch(lastProduct === "map" ? "explore" : lastProduct);
  }

  const now = useMemo(
    () => new Date().toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    }).toUpperCase(),
    []
  );

  if (error) {
    return (
      <Shell><Hint title="Couldn’t load the command center" body={error} /></Shell>
    );
  }
  if (!cc) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-24 text-fg-secondary">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  const { account, kpis, attention = [], portfolio = [], products_summary = {}, activity = [] } = cc;
  const acctInitials = (account?.name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const kpiList = KPI_META.filter(([k]) => kpis?.[k] != null);
  const suite = PRODUCT_ORDER.filter((code) => products_summary[code]);

  // Type filter — pills built only from the types actually present in the
  // portfolio (plus "Unspecified" when any project has no type). One source of
  // truth (PROJECT_TYPES); filtering is client-side (portfolio already loaded).
  const presentTypes = new Set(portfolio.map((p) => p.type ?? null));
  const typePills = [
    { key: "all", label: "All" },
    ...PROJECT_TYPES.filter((t) => presentTypes.has(t.key)).map((t) => ({ key: t.key, label: t.label })),
    ...(presentTypes.has(null) ? [{ key: "__unspecified__", label: UNSPECIFIED_LABEL }] : []),
  ];
  const visiblePortfolio = portfolio.filter((p) =>
    typeFilter === "all" ? true
      : typeFilter === "__unspecified__" ? (p.type ?? null) === null
        : p.type === typeFilter
  );

  return (
    <Shell>
      {/* header */}
      <div className="flex items-center gap-3.5 pb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-[30px] h-[30px] rounded-lg bg-[#0d3a52] text-accent-info flex items-center justify-center font-bold text-[12px]">{acctInitials}</span>
          <span className="font-semibold text-fg-primary">{account?.name}</span>
          {account?.scope_label && (
            <span className="text-[9px] font-mono uppercase tracking-[0.1em] text-accent-info bg-[#0d3a52]/40 border border-teal-500/20 px-2 py-0.5 rounded-md">{account.scope_label}</span>
          )}
        </div>
        <div className="ml-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-500">Command Center</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {canCreateProject && (
            <button onClick={() => nav.goMapTab("projects")} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent-action/20 border border-accent-action/40 text-accent-action hover:bg-accent-action/30 transition">
              <Plus size={11} /> New project
            </button>
          )}
          <div className="text-[10px] font-mono text-slate-500">{now}</div>
        </div>
      </div>

      {/* resume last session */}
      {lastProduct && (
        <button onClick={handleResume}
          className="w-full flex items-center gap-3 bg-bg-soft border border-slate-700 rounded-lg px-4 py-3 mb-[18px] hover:border-accent-action/40 hover:bg-bg-softer transition text-left group">
          <span className="w-7 h-7 rounded-md bg-accent-action/15 text-accent-action flex items-center justify-center shrink-0"><History size={14} /></span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-fg-secondary">Resume</div>
            <div className="text-[13px] text-fg-primary font-medium truncate">
              {RESUME_LABEL[lastProduct] ?? lastProduct}{resumeProjectName ? ` · ${resumeProjectName}` : ""}
            </div>
          </div>
          <span className="text-[10px] font-mono font-semibold text-accent-info group-hover:text-accent-info flex items-center gap-1 shrink-0">
            Continue <ArrowRight size={11} />
          </span>
        </button>
      )}

      {/* KPI strip */}
      {kpiList.length > 0 && (
        <div className="grid gap-px bg-slate-800 border border-slate-700 rounded-lg overflow-hidden mb-[18px]"
             style={{ gridTemplateColumns: `repeat(${kpiList.length}, minmax(0,1fr))` }}>
          {kpiList.map(([k, label, alert]) => (
            <div key={k} className="bg-bg-soft px-4 py-3">
              <div className="text-[9px] font-mono uppercase tracking-[0.1em] text-fg-secondary">{label}</div>
              <div className={`text-[21px] font-semibold mt-1.5 tabular-nums ${alert ? "text-amber-400" : "text-fg-primary"}`}
                   style={k === "latest_imagery" ? { fontSize: 15 } : undefined}>
                {k === "latest_imagery" ? fmtEpoch(kpis[k]) : Number(kpis[k]).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* attention hero */}
      {attention.length > 0 && (
        <div className="bg-bg-soft border border-slate-700 rounded-[10px] mb-[18px] overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-800">
            <span className="w-[7px] h-[7px] rounded-full bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.13)]" />
            <h2 className="text-[13px] font-semibold text-fg-primary">Needs your attention</h2>
            <span className="ml-auto text-[10px] font-mono text-fg-secondary">{attention.length} {attention.length === 1 ? "item" : "items"}</span>
          </div>
          {attention.map((a, i) => {
            const meta = PRODUCT_META[a.product] || {};
            return (
              <button key={i} onClick={() => launch(meta.slug)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b border-slate-800 last:border-0 hover:bg-bg-softer transition text-left group">
                <span className={`text-[8px] font-mono font-bold tracking-wide px-1.5 py-1 rounded w-[74px] text-center shrink-0 ${meta.chip}`}>{meta.label}</span>
                <span className="text-[17px] font-bold tabular-nums w-[30px] text-right shrink-0 text-fg-primary">{a.count}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-fg-primary">{a.title}</div>
                  {a.detail && <div className="text-[11px] text-fg-secondary mt-0.5 truncate">{a.detail}</div>}
                </div>
                <span className="text-[10px] font-mono font-semibold text-accent-action group-hover:text-accent-action flex items-center gap-1 shrink-0">
                  {a.action || "Open"} <ArrowRight size={11} />
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* condensed three-panel row: Projects | Products | PadRekko activity.
          Each panel is a fixed-max-height, independently-scrollable card so the
          region's footprint stays small as any list grows. Products gets extra
          width (its rows carry inline stats). */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-4 items-start max-[900px]:grid-cols-1">

        {/* Projects panel */}
        <div className="bg-bg-soft border border-slate-800 rounded-[10px] overflow-hidden">
          <div className="px-3.5 py-3 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-secondary">Projects</span>
              {portfolio.length > 0 && (
                <button onClick={() => nav.goMapTab("projects")} className="text-[9px] font-mono text-accent-action">View all {portfolio.length} →</button>
              )}
            </div>
            {typePills.length > 2 && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {typePills.map((t) => (
                  <button key={t.key} onClick={() => setTypeFilter(t.key)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                      typeFilter === t.key
                        ? "border-accent-action/60 text-accent-action"
                        : "border-slate-700 text-fg-secondary hover:border-slate-600 hover:text-fg-primary"
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {portfolio.length === 0 ? (
              <Hint small title="No projects yet" body={canCreateProject ? "Create a project to get started." : "No projects assigned to your organization."} />
            ) : (
              visiblePortfolio.map((p) => (
                <button key={p.id} onClick={() => openProject(p.id)}
                  className="w-full flex items-center gap-2 px-3.5 py-2.5 border-b border-slate-800 last:border-0 hover:bg-bg-softer transition text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[12.5px] font-semibold text-fg-primary truncate">{p.name}</span>
                      {/* type badge — purpose (distinct from the product chips) */}
                      <span title={projectTypeTooltip(p.type)}
                        className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full border border-violet-400/30 bg-violet-400/10 text-violet-200 shrink-0">
                        <FolderOpen size={8} className="opacity-70" />
                        {projectTypeLabel(p.type)}
                      </span>
                    </div>
                    {p.status && <div className="text-[10px] text-accent-info font-medium mt-0.5">{p.status}</div>}
                  </div>
                  <ChevronRight size={14} className="text-slate-500 shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Products panel */}
        <div className="bg-bg-soft border border-slate-800 rounded-[10px] overflow-hidden">
          <div className="px-3.5 py-3 border-b border-slate-800">
            <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-secondary">Products</span>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {suite.length === 0 ? (
              <div className="px-3.5 py-8 text-center text-[11px] text-fg-secondary">No products.</div>
            ) : (
              suite.map((code) => {
                const meta = PRODUCT_META[code];
                const s = products_summary[code];
                return (
                  <button key={code} onClick={() => launch(meta.slug)}
                    className="w-full flex items-center gap-3 px-3.5 py-3 border-b border-slate-800 last:border-0 hover:bg-bg-softer transition text-left group">
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${meta.chip}`}><meta.Icon size={14} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-fg-primary">{meta.label}</div>
                      <div className="text-[10px] font-mono text-fg-secondary mt-0.5 truncate">
                        {meta.stats.map(([key, label, tone], idx) => (
                          <span key={key}>
                            {idx > 0 && <span className="text-slate-600"> · </span>}
                            <span className={`tabular-nums ${tone === "warn" ? "text-amber-400" : tone === "good" ? "text-emerald-400" : "text-fg-primary"}`}>
                              {Number(s[key] ?? 0).toLocaleString()}
                            </span>{" "}{label.toLowerCase().replace(/\.$/, "")}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ArrowRight size={13} className="text-slate-500 group-hover:text-accent-info shrink-0" />
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* PadRekko activity panel (real collab_events) */}
        <div className="bg-bg-soft border border-slate-800 rounded-[10px] overflow-hidden">
          <div className="px-3.5 py-3 border-b border-slate-800 flex items-center gap-2">
            <Activity size={12} className="text-fg-secondary" />
            <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-secondary">PadRekko activity</span>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {activity.length === 0 ? (
              <div className="px-3.5 py-8 text-center text-[11px] text-fg-secondary">No recent activity.</div>
            ) : (
              activity.map((e, i) => (
                <div key={i} className="flex gap-2.5 px-3.5 py-3 border-b border-slate-800 last:border-0">
                  <span className="w-[5px] h-[5px] rounded-full bg-accent-info shrink-0 mt-1.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">PadRekko</span>
                    <div className="text-[11px] text-fg-primary font-medium mt-1 leading-snug">{e.title}</div>
                    {e.detail && <div className="text-[9px] font-mono text-slate-500 mt-0.5">{fmtTitleCase(e.detail)}</div>}
                  </div>
                  <span className="text-[9px] font-mono text-slate-500 shrink-0 mt-1">{timeAgo(e.at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-[18px] text-[11px] text-slate-500">
        Composed from your account’s products. Every figure is live — no sample data.
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  // Full-width, matching the PadRekko project dashboard's container exactly
  // (w-full px-8 py-5) so the two read as sibling surfaces — NOT the standalone
  // mockup's centered max-w-[1180px] box.
  return (
    <div className="h-full w-full overflow-y-auto bg-bg text-gray-100">
      <div className="w-full px-8 py-5">{children}</div>
    </div>
  );
}

function Hint({ title, body, small }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center gap-2 ${small ? "py-10" : "py-24"} px-6`}>
      <div className="text-fg-secondary"><FolderOpen size={small ? 18 : 22} /></div>
      <div className="text-[14px] font-medium text-fg-primary">{title}</div>
      <div className="text-[12px] text-fg-secondary max-w-[420px] leading-relaxed">{body}</div>
    </div>
  );
}
